import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { parseEnvNumberList } from '../../../shared/utils/envParse';
import { useAppSelector } from '@/store/hooks';
import { getAllTagsForWorkflow } from '@/services/qc/workflowTagsService';
import {
  ASSIGNED_REVIEW_TASK_ASSIGNMENTS_STALE_MS,
  ASSIGNED_REVIEW_TASK_BATCHES_STALE_MS,
  ASSIGNED_REVIEW_TASK_DEFAULT_PAGE_SIZE_OPTIONS,
  ASSIGNED_REVIEW_TASK_FALLBACK_DEFAULT_PAGE_SIZE,
  ASSIGNED_REVIEW_TASK_WORKFLOWS_STALE_MS,
} from '../constants/assignedReviewTaskPageConstants';
import {
  getAssignedRequests,
  getUserBatches,
  getUserWorkflows,
} from '../api';
import { reqStatusFromTab } from '../utils/assignedReviewTaskTabUtils';
import {
  buildTableFiltersFromSearchParams,
  normalizeTabFromSearchParam,
} from '../utils/assignedReviewTaskUrlUtils';

const FILTER_PARAM_KEYS = [
  'language',
  'district',
  'state',
  'duration',
  'file_name',
  'source_file',
  'search',
];

function readDefaultPageSizeFromEnv() {
  const n = Number(import.meta.env.VITE_QC_TASK_DEFAULT_PAGE_SIZE);
  return Number.isFinite(n) && n > 0
    ? n
    : ASSIGNED_REVIEW_TASK_FALLBACK_DEFAULT_PAGE_SIZE;
}

export function useAssignedReviewTaskPageController() {
  const [searchParams, setSearchParams] = useSearchParams();

  const [activeTab, setActiveTab] = useState(() =>
    normalizeTabFromSearchParam(searchParams.get('tab')),
  );
  const [workflowId, setWorkflowId] = useState(
    () => searchParams.get('workflowId') ?? '',
  );
  const [batchId, setBatchId] = useState(() => searchParams.get('batchId') ?? '');

  const [pagination, setPagination] = useState(() => ({
    pageIndex: 0,
    pageSize: readDefaultPageSizeFromEnv(),
  }));
  const [sorting, setSorting] = useState([]);

  const sortBy = sorting?.[0]?.id ?? 'file_name';
  const sortDirection = sorting?.[0]?.desc ? 'DESC' : 'ASC';

  const pageSizeOptions = useMemo(
    () =>
      parseEnvNumberList(
        import.meta.env.VITE_QC_TASK_PAGE_SIZES,
        ASSIGNED_REVIEW_TASK_DEFAULT_PAGE_SIZE_OPTIONS,
      ),
    [],
  );

  const defaultPageSize = useMemo(() => readDefaultPageSizeFromEnv(), []);

  const activeReqStatus = useMemo(
    () => reqStatusFromTab(activeTab),
    [activeTab],
  );

  const tableFilters = useMemo(
    () => buildTableFiltersFromSearchParams(searchParams),
    [searchParams],
  );

  const applyTableFiltersToUrl = useCallback(
    (nextFilters) => {
      const next = new URLSearchParams(searchParams);
      FILTER_PARAM_KEYS.forEach((k) => next.delete(k));

      Object.entries(nextFilters ?? {}).forEach(([k, v]) => {
        if (!FILTER_PARAM_KEYS.includes(k)) return;
        const s = v == null ? '' : String(v).trim();
        if (!s) return;
        next.set(k, s);
      });

      setPagination((prev) => ({ ...prev, pageIndex: 0 }));
      setSearchParams(next, { replace: true });
    },
    [searchParams, setSearchParams],
  );

  const {
    data: workflowsApiData = [],
    isLoading: isWorkflowLoading,
    isFetching: isWorkflowFetching,
    error: workflowsError,
  } = useQuery({
    queryKey: ['reviewUserWorkflows', activeReqStatus],
    queryFn: () => getUserWorkflows(activeReqStatus),
    enabled: Boolean(activeReqStatus),
    staleTime: ASSIGNED_REVIEW_TASK_WORKFLOWS_STALE_MS,
    gcTime: ASSIGNED_REVIEW_TASK_WORKFLOWS_STALE_MS * 2,
  });

  const globalWorkflows = useAppSelector((state) => state.workflowTags.globalWorkflows);
  const tagsByWorkflowId = useAppSelector((state) => state.workflowTags.tagsByWorkflowId);

  const workflowOptions = useMemo(() => {
    const ids = Array.isArray(workflowsApiData) ? workflowsApiData : [];
    return ids.map((apiRow) => {
      const full = Array.isArray(globalWorkflows)
        ? globalWorkflows.find((g) => g.workflow_id === apiRow.workflow_id)
        : null;
      return full ? { ...apiRow, ...full } : apiRow;
    });
  }, [workflowsApiData, globalWorkflows]);

  const {
    data: batchesApiData = [],
    isLoading: isBatchesLoading,
    isFetching: isBatchesFetching,
    isSuccess: isBatchesSuccess,
    error: batchesError,
  } = useQuery({
    queryKey: ['reviewUserBatches', workflowId, activeReqStatus],
    queryFn: () => getUserBatches({ workflowId, reqStatus: activeReqStatus }),
    enabled: Boolean(workflowId && activeReqStatus),
    staleTime: ASSIGNED_REVIEW_TASK_BATCHES_STALE_MS,
    gcTime: ASSIGNED_REVIEW_TASK_BATCHES_STALE_MS * 2,
  });

  const batchOptions = useMemo(() => {
    if (!workflowId) return [];
    return Array.isArray(batchesApiData) ? batchesApiData : [];
  }, [batchesApiData, workflowId]);

  const {
    data: assignmentsPayload,
    isLoading: isAssignmentsLoading,
    isFetching: isAssignmentsFetching,
    refetch: refetchAssignments,
    error: assignmentsError,
  } = useQuery({
    queryKey: [
      'reviewAssignedRequests',
      activeReqStatus,
      workflowId,
      batchId,
      pagination.pageIndex,
      pagination.pageSize,
      tableFilters,
      sortBy,
      sortDirection,
    ],
    queryFn: () =>
      getAssignedRequests({
        reqStatus: activeReqStatus,
        page: pagination.pageIndex + 1,
        limit: pagination.pageSize,
        batchId,
        workflowId,
        filters: tableFilters,
        sortBy,
        sortDirection,
      }),
    enabled: Boolean(activeReqStatus && workflowId && batchId),
    staleTime: ASSIGNED_REVIEW_TASK_ASSIGNMENTS_STALE_MS,
    gcTime: ASSIGNED_REVIEW_TASK_ASSIGNMENTS_STALE_MS * 2,
  });

  const handleSortingChange = useCallback(
    (updater) => {
      setSorting((prev) =>
        typeof updater === 'function' ? updater(prev) : updater,
      );
      setPagination((p) => ({ ...p, pageIndex: 0 }));
    },
    [setPagination],
  );

  const urlQueryKey = searchParams.toString();
  useEffect(() => {
    const params = new URLSearchParams(urlQueryKey);
    const t = normalizeTabFromSearchParam(params.get('tab'));
    const w = params.get('workflowId') ?? '';
    const b = params.get('batchId') ?? '';
    setActiveTab((cur) => (cur === t ? cur : t));
    setWorkflowId((cur) => (cur === w ? cur : w));
    setBatchId((cur) => (cur === b ? cur : b));
  }, [urlQueryKey]);

  useEffect(() => {
    if (workflowOptions.length > 0 && !workflowId) {
      setWorkflowId(workflowOptions[0]?.workflow_id ?? '');
    }
  }, [workflowOptions, workflowId]);

  useEffect(() => {
    if (isBatchesSuccess && batchOptions.length > 0 && !batchId) {
      setBatchId(batchOptions[0]?.batch_id ?? '');
    }
  }, [batchOptions, isBatchesSuccess, batchId]);

  useEffect(() => {
    setPagination((prev) =>
      prev.pageIndex === 0 ? prev : { ...prev, pageIndex: 0 },
    );
  }, [activeTab, workflowId, batchId]);

  useEffect(() => {
    const next = new URLSearchParams();
    next.set('tab', activeTab);
    if (workflowId) next.set('workflowId', workflowId);
    if (batchId) next.set('batchId', batchId);
    Object.entries(tableFilters).forEach(([key, value]) => {
      if (value) next.set(key, value);
    });

    const current = searchParams.toString();
    const upcoming = next.toString();
    if (current !== upcoming) {
      setSearchParams(next, { replace: true });
    }
  }, [
    activeTab,
    workflowId,
    batchId,
    tableFilters,
    searchParams,
    setSearchParams,
  ]);

  const handleTabChange = useCallback((nextTab) => {
    setActiveTab(nextTab);
    setWorkflowId('');
    setBatchId('');
  }, []);

  const handleWorkflowChange = useCallback((newValue) => {
    setWorkflowId(newValue?.workflow_id ?? '');
    setBatchId('');
  }, []);

  const handleBatchChange = useCallback((newValue) => {
    setBatchId(newValue?.batch_id ?? '');
  }, []);

  const assignmentsQueryEnabled = Boolean(activeReqStatus && workflowId && batchId);

  const rows = assignmentsQueryEnabled ? (assignmentsPayload?.rows ?? []) : [];
  const rowCount = assignmentsQueryEnabled
    ? (assignmentsPayload?.totalCount ?? 0)
    : 0;

  const workflowValue =
    workflowOptions.find((w) => w.workflow_id === workflowId) ?? null;
  const batchValue = batchOptions.find((b) => b.batch_id === batchId) ?? null;

  const workflowType = workflowValue?.workflow_type ?? null;
  const isAnnotation = workflowType === 'ANNOTATION';

  const { data: apiTagData = [] } = useQuery({
    queryKey: ['reviewTagsForWorkflow', workflowId],
    queryFn: () => getAllTagsForWorkflow(workflowId),
    enabled: Boolean(workflowId),
    staleTime: 5 * 60 * 1000,
  });

  const tagOptions = useMemo(() => {
    const fromRedux = (() => {
      if (!workflowId) return [];
      const rows = tagsByWorkflowId[workflowId];
      return Array.isArray(rows) ? rows.map((r) => r.tag_name).filter(Boolean) : [];
    })();
    const fromApi = Array.isArray(apiTagData)
      ? apiTagData.map((r) => r.tag_name).filter(Boolean)
      : [];
    return [...new Set([...fromRedux, ...fromApi])];
  }, [tagsByWorkflowId, workflowId, apiTagData]);

  const errorMessage =
    workflowsError?.message ||
    batchesError?.message ||
    assignmentsError?.message ||
    '';

  const tableLoading =
    assignmentsQueryEnabled && (isAssignmentsLoading || isAssignmentsFetching);

  return {
    activeTab,
    workflowId,
    batchId,
    workflowValue,
    batchValue,
    workflowOptions,
    batchOptions,
    handleTabChange,
    handleWorkflowChange,
    handleBatchChange,
    isWorkflowLoading,
    isWorkflowFetching,
    isBatchesLoading,
    isBatchesFetching,
    workflowType,
    isAnnotation,
    tagOptions,
    rows,
    rowCount,
    tableLoading,
    tableFilters,
    applyTableFiltersToUrl,
    pagination,
    setPagination,
    refetchAssignments,
    pageSizeOptions,
    defaultPageSize,
    errorMessage,
    sorting,
    onSortingChange: handleSortingChange,
  };
}
